"use client";

import React, { useMemo, useState } from "react";
import { debugArray } from "../../lib/debugArray";
import type { UploadEntry } from "./types";

function getUploadStatusLabel(status: UploadEntry["status"]) {
  switch (status) {
    case "queued":
      return "Queued";
    case "uploading":
      return "Uploading";
    case "uploaded":
      return "Upload successful";
    case "error":
      return "Upload unsuccessful";
    case "removing":
      return "Removing";
  }
}

function getUploadStatusClass(status: UploadEntry["status"]) {
  switch (status) {
    case "uploaded":
      return "border-emerald-200 bg-emerald-50 text-emerald-800";
    case "error":
      return "border-red-200 bg-red-50 text-red-700";
    case "uploading":
      return "border-cyan-200 bg-cyan-50 text-cyan-800";
    default:
      return "border-slate-200 bg-slate-50 text-slate-700";
  }
}

function formatAcceptList(accept: string) {
  return accept
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean)
    .join(", ");
}

function parseAcceptList(accept?: string) {
  if (!accept) return [];
  return accept
    .split(",")
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);
}

function matchesAccept(file: File, acceptRules: string[]) {
  if (!acceptRules.length) return true;

  const fileName = file.name.toLowerCase();
  const fileType = file.type.toLowerCase();

  return acceptRules.some((rule) => {
    if (rule.startsWith(".")) {
      return fileName.endsWith(rule);
    }

    if (rule.endsWith("/*")) {
      const prefix = rule.slice(0, -1);
      return fileType.startsWith(prefix);
    }

    return fileType === rule;
  });
}

export function FileDropzone({
  name,
  label,
  required,
  helper,
  accept,
  multiple = true,
  files,
  setFiles,
  uploadEntries = [],
  onRemoveFile,
  disablePicker,
  isInvalid,
  maxFiles,
  maxTotalBytes,
  onLimitExceeded,
}: {
  name: string;
  label: string;
  required?: boolean;
  helper?: string;
  accept?: string;
  multiple?: boolean;
  files: File[];
  setFiles: (next: File[]) => void;
  uploadEntries?: UploadEntry[];
  onRemoveFile?: (idx: number) => void;
  disablePicker?: boolean;
  isInvalid?: boolean;
  maxFiles?: number;
  maxTotalBytes?: number;
  onLimitExceeded?: (title: string, message: string) => void;
}) {
  const [isDragActive, setIsDragActive] = useState(false);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const acceptRules = useMemo(() => parseAcceptList(accept), [accept]);

  function checkSelectionLimit(nextFiles: File[]) {
    if (typeof maxFiles !== "number" && typeof maxTotalBytes !== "number") {
      return true;
    }

    const totalSize = nextFiles.reduce((acc, file) => acc + file.size, 0);
    const tooManyFiles = typeof maxFiles === "number" && nextFiles.length > maxFiles;
    const tooLarge = typeof maxTotalBytes === "number" && totalSize > maxTotalBytes;

    if (!tooManyFiles && !tooLarge) {
      return true;
    }

    const limitBits: string[] = [];
    if (tooManyFiles) {
      limitBits.push(`Max number of files is ${maxFiles}.`);
    }
    if (tooLarge) {
      limitBits.push(`Max size support is ${(maxTotalBytes / 1024 / 1024).toFixed(0)} MB.`);
    }

    onLimitExceeded?.(
      `${label} upload limit reached`,
      `${limitBits.join(" ")} Please remove some files and try again.`,
    );
    return false;
  }

  function validateFiles(nextFiles: File[]) {
    const acceptedFiles = nextFiles.filter((file) => matchesAccept(file, acceptRules));
    const rejectedFiles = nextFiles.filter((file) => !matchesAccept(file, acceptRules));

    if (!rejectedFiles.length) {
      setSelectionError(null);
      return acceptedFiles;
    }

    const rejectedNames = rejectedFiles.map((file) => file.name).join(", ");
    setSelectionError(
      accept
        ? `Unsupported file type removed: ${rejectedNames}. Accepted types: ${formatAcceptList(accept)}.`
        : `Unsupported file type removed: ${rejectedNames}.`,
    );
    return acceptedFiles;
  }

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    const validPicked = validateFiles(multiple ? picked : picked.slice(0, 1));
    const merged = multiple ? [...files, ...validPicked] : validPicked.slice(0, 1);
    if (!merged.length) {
      e.currentTarget.value = "";
      return;
    }
    if (!checkSelectionLimit(merged)) {
      e.currentTarget.value = "";
      return;
    }
    setFiles(merged);
    e.currentTarget.value = "";
  }

  function removeFile(idx: number) {
    if (onRemoveFile) {
      onRemoveFile(idx);
      return;
    }
    setFiles(files.filter((_, i) => i !== idx));
  }

  function onDrop(e: React.DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    setIsDragActive(false);
    const dropped = Array.from(e.dataTransfer.files || []);
    if (!dropped.length) return;
    const validDropped = validateFiles(multiple ? dropped : dropped.slice(0, 1));
    const merged = multiple ? [...files, ...validDropped] : validDropped.slice(0, 1);
    if (!merged.length) {
      return;
    }
    if (!checkSelectionLimit(merged)) {
      return;
    }
    setFiles(merged);
  }

  const totalSize = useMemo(() => files.reduce((acc, f) => acc + f.size, 0), [files]);

  return (
    <div className="min-w-0 space-y-2">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3">
        <p className="text-sm font-medium text-black sm:pr-4">
          {label} {required ? <span className="text-red-500">*</span> : null}
        </p>
        <span className="text-xs text-black">
          {files.length ? `${files.length} file(s) - ${(totalSize / 1024 / 1024).toFixed(2)} MB` : ""}
        </span>
      </div>

      {helper ? <p className="text-xs text-black">{helper}</p> : null}
      {selectionError ? <p className="text-xs font-medium text-rose-600">{selectionError}</p> : null}

      <label
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragActive(true);
        }}
        onDragLeave={() => setIsDragActive(false)}
        onDrop={onDrop}
        data-invalid={isInvalid ? "true" : "false"}
        className={[
          "group flex min-w-0 max-w-full cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed px-4 py-6 text-center transition sm:px-6 sm:py-8",
          isDragActive ? "border-cyan-400 bg-cyan-50" : "border-indigo-300 bg-white",
          isInvalid ? "border-red-500 ring-1 ring-red-500" : "",
          "hover:bg-indigo-50",
        ].join(" ")}
      >
        <input
          name={name}
          type="file"
          className="hidden"
          multiple={multiple}
          accept={accept}
          disabled={disablePicker}
          onChange={onPick}
        />
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-cyan-100 text-cyan-700">
          <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
            <path
              fill="currentColor"
              d="M7 13h4V5h2v8h4l-5 5-5-5zM5 19h14v2H5v-2z"
            />
          </svg>
        </div>
        <div className="mt-3 text-sm font-semibold text-black">
          Drop files here or click to choose
        </div>
        <div className="mt-1 text-xs text-black">
          {disablePicker
            ? "Uploads are in progress. Please wait."
            : multiple
              ? "You can upload multiple files."
              : "Single file only."}
        </div>
        {files.length > 0 ? (
          <div className="mt-3 w-full rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-left">
            {accept ? (
              <div className="mb-2 break-all text-[11px] uppercase tracking-[0.16em] text-slate-500">
                Accepts: {formatAcceptList(accept)}
              </div>
            ) : null}
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-indigo-700">
              Selected file{files.length > 1 ? "s" : ""}
            </div>
            <div className="mt-1 space-y-2">
              {debugArray(`file-dropzone.${name}.selected`, files).map((file, idx) => (
                <div key={`${file.name}-${idx}`} className="rounded-lg border border-indigo-200 bg-white px-3 py-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-black">{file.name}</div>
                      <div className="text-xs text-black">{(file.size / 1024 / 1024).toFixed(2)} MB</div>
                      {uploadEntries[idx] ? (
                        <div className="mt-2 space-y-1.5">
                          <span
                            className={[
                              "inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold",
                              getUploadStatusClass(uploadEntries[idx].status),
                            ].join(" ")}
                          >
                            {getUploadStatusLabel(uploadEntries[idx].status)}
                          </span>
                          {uploadEntries[idx].status === "queued" ? (
                            <p className="text-xs italic text-blue-700" role="status">
                              File selected and queued. It will upload to the server when you submit the form.
                            </p>
                          ) : null}
                          {uploadEntries[idx].status === "error" ? (
                            <p className="text-xs italic text-red-700" role="status">
                              Upload was unsuccessful. Please try again.
                            </p>
                          ) : null}
                          {uploadEntries[idx].error ? (
                            <div className="text-xs text-red-500">{uploadEntries[idx].error}</div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(idx)}
                      disabled={uploadEntries[idx]?.status === "removing"}
                      className="rounded-lg border border-indigo-300 px-3 py-1.5 text-xs font-semibold text-black hover:bg-indigo-50"
                    >
                      {uploadEntries[idx]?.status === "removing" ? "Removing..." : "Remove"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </label>
      {isInvalid ? (
        <p className="flex items-center gap-1.5 text-sm font-medium text-red-500">
          <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" aria-hidden="true">
            <path
              fill="currentColor"
              d="M12 3 1.8 20.5A1 1 0 0 0 2.66 22h18.68a1 1 0 0 0 .86-1.5L12 3zm1 14h-2v-2h2v2zm0-4h-2V8h2v5z"
            />
          </svg>
          <span>Your answer is required.</span>
        </p>
      ) : null}
    </div>
  );
}
